import React, { createContext, useContext, useEffect, useState } from 'react';
import { Organization, Topic } from '../../client/interfaces';
import client from '../../client/client';
import { useMessage } from '../Messages/MessageContext';
import { useAuth } from '../AuthContext/AuthContext';

interface HelpdeskParams {
  search: string;
  topics: Topic[];
  organizations: Organization[];
}

interface HelpdeskContextType {
  params: HelpdeskParams;
  setParams: React.Dispatch<React.SetStateAction<HelpdeskParams>>;
  topics: Topic[];
  organizations: Organization[];
  totalCount: number;
  setTotalCount: React.Dispatch<React.SetStateAction<number>>;
  emptyParams: HelpdeskParams;
  resetFilters: () => void;
  refreshTopicsAndOrganizations: () => Promise<void>;
}

const emptyParams: HelpdeskParams = {
  search: '',
  topics: [],
  organizations: [],
};

const HelpdeskContext = createContext<HelpdeskContextType>({
  params: emptyParams,
  setParams: () => {},
  topics: [],
  organizations: [],
  totalCount: 0,
  setTotalCount: () => {},
  emptyParams,
  resetFilters: () => {},
  refreshTopicsAndOrganizations: async () => {},
});

export const useHelpdesk = () => useContext(HelpdeskContext);

export const HelpdeskProvider = ({ children }) => {
  const { me } = useAuth();
  const { setMessage } = useMessage();
  const [params, setParams] = useState<HelpdeskParams>({ ...emptyParams });
  const [topics, setTopics] = useState<Topic[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [totalCount, setTotalCount] = useState(0);

  const refreshTopicsAndOrganizations = async () => {
    try {
      const [topicsData, orgsData] = await Promise.all([
        client.getActiveHelpdeskTopics(),
        client.getActiveHelpdeskOrganizations(),
      ]);
      setTopics(topicsData);
      setOrganizations(orgsData);
    } catch (error) {
      setMessage({ message: error.message, type: 'error' });
    }
  };

  useEffect(() => {
    if (!me) return;
    refreshTopicsAndOrganizations();
  }, [me]);

  const resetFilters = () => {
    setParams({ ...emptyParams });
  };

  return (
    <HelpdeskContext.Provider
      value={{
        params,
        setParams,
        topics,
        organizations,
        totalCount,
        setTotalCount,
        emptyParams,
        resetFilters,
        refreshTopicsAndOrganizations,
      }}
    >
      {children}
    </HelpdeskContext.Provider>
  );
};

export { HelpdeskContext };
